import cn from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import {
  addElementToReflowRowOperation,
  addReflowRowOperation,
  deleteReflowGroupOperation,
  removeElementFromReflowRowOperation,
  removeReflowRowOperation,
  renameReflowGroupOperation,
  setReflowRowDividerOperation,
  setReflowRowHorizontalOperation,
  setReflowRowOptionalOperation,
} from 'editor/src/store/design/operation/reflowGroupOperations';
import { ReflowGroup, ReflowRow } from 'editor/src/store/design/types';
import { isReflowMember, ReflowMember } from 'editor/src/store/design/util/reflowMember';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import useTr from 'editor/src/util/useTr';

import styles from './index.module.scss';

const typeLabel: Record<Exclude<ReflowMember['type'], 'text'>, string> = {
  line: 'Line',
  rectangle: 'Rectangle',
  image: 'Image',
};

const labelFor = (element: ReflowMember): string => {
  if (element.type === 'text') {
    return element.name || (element.extra?.text ? `“${element.extra.text}”` : `Text #${element.uuid}`);
  }
  return element.name || `${typeLabel[element.type]} #${element.uuid}`;
};

interface ChipProps {
  element: ReflowMember;
  onRemove: () => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, uuid: number) => void;
  isDivider?: boolean;
  showDividerToggle?: boolean;
  onToggleDivider?: () => void;
}

function ElementChip({
  element,
  onRemove,
  draggable,
  onDragStart,
  isDivider,
  showDividerToggle,
  onToggleDivider,
}: ChipProps) {
  const tr = useTr();
  // Only text can ever be edited by the buyer — a line/rectangle/image member is
  // always decorative, so it is always tagged "fixed" regardless of its own
  // personalizationLocked flag.
  const isEditable = element.type === 'text' && !element.personalizationLocked;
  return (
    <span
      className={cn(styles.chip, isEditable ? styles.chipValue : styles.chipLabel, isDivider && styles.chipDivider)}
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, element.uuid)}
      title={
        isEditable
          ? tr('editor-reflow-row-tag-editable-tooltip', 'Buyer can edit this text')
          : tr('editor-reflow-row-tag-fixed-tooltip', 'Static text, not editable by the buyer')
      }
    >
      <span className={styles.chipDot} aria-hidden />
      <span className={styles.chipName}>{labelFor(element)}</span>
      {showDividerToggle && (
        <button
          type="button"
          className={cn(styles.chipDividerToggle, isDivider && styles.chipDividerToggleActive)}
          onClick={(e) => {
            e.stopPropagation();
            onToggleDivider?.();
          }}
          title={
            isDivider
              ? tr('editor-reflow-divider-on', 'Divider — hidden unless both neighbours are filled. Click to unset.')
              : tr('editor-reflow-divider-off', 'Mark as divider (hidden unless both neighbours are filled)')
          }
        >
          |
        </button>
      )}
      <button
        type="button"
        className={styles.chipRemove}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        aria-label={tr('editor-reflow-remove-element', 'Remove from row')}
      >
        ×
      </button>
    </span>
  );
}

interface PickerProps {
  candidates: ReflowMember[];
  onPick: (uuid: number) => void;
  onClose: () => void;
}

function ElementPicker({ candidates, onPick, onClose }: PickerProps) {
  const tr = useTr();
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={onClose} role="presentation" />
      <div className={styles.picker}>
        {candidates.length === 0 && (
          <div className={styles.empty}>
            {tr('editor-reflow-no-candidates-any', 'No more elements on this spread.')}
          </div>
        )}
        {candidates.map((c) => {
          const isEditable = c.type === 'text' && !c.personalizationLocked;
          return (
            <div
              key={c.uuid}
              className={styles.pickerOption}
              onClick={() => {
                onPick(c.uuid);
                onClose();
              }}
              role="button"
              tabIndex={0}
            >
              <span>{labelFor(c)}</span>
              <span className={styles.pickerTag}>
                {isEditable
                  ? tr('editor-reflow-row-tag-editable', 'editable')
                  : tr('editor-reflow-row-tag-fixed', 'fixed')}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

interface RowCardProps {
  group: ReflowGroup;
  row: ReflowRow;
  rowIndex: number;
  spreadIndex: number;
  reflowMembers: ReflowMember[];
  onElementDrop: (targetRowId: string, draggedUuid: number) => void;
}

function RowCard({ group, row, rowIndex, spreadIndex, reflowMembers, onElementDrop }: RowCardProps) {
  const dispatch = useDispatch();
  const tr = useTr();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const elementsInRow = useMemo(
    () =>
      row.elementUuids.map((uuid) => reflowMembers.find((el) => el.uuid === uuid)).filter(Boolean) as ReflowMember[],
    [row.elementUuids, reflowMembers],
  );

  const candidates = useMemo(
    () => reflowMembers.filter((el) => !row.elementUuids.includes(el.uuid)),
    [reflowMembers, row.elementUuids],
  );

  const handleDragStart = (e: React.DragEvent, uuid: number) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/x-reflow-element-uuid', String(uuid));
  };

  return (
    <div
      className={cn(styles.row, dragOver && styles.rowDragOver)}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('text/x-reflow-element-uuid')) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDragOver(true);
        }
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        setDragOver(false);
        const raw = e.dataTransfer.getData('text/x-reflow-element-uuid');
        const uuid = Number(raw);
        if (!Number.isNaN(uuid)) {
          e.preventDefault();
          onElementDrop(row.id, uuid);
        }
      }}
    >
      <div className={styles.rowNumber}>{rowIndex + 1}</div>
      <button
        type="button"
        className={cn(styles.horizontalToggle, row.horizontalReflow && styles.horizontalToggleActive)}
        onClick={() => dispatch(setReflowRowHorizontalOperation(spreadIndex, group.id, row.id, !row.horizontalReflow))}
        title={
          row.horizontalReflow
            ? tr('editor-reflow-row-horizontal-on', 'Horizontal reflow enabled — elements pack left-to-right')
            : tr('editor-reflow-row-horizontal-off', 'Enable horizontal reflow for this row')
        }
      >
        ↔
      </button>
      <button
        type="button"
        className={cn(styles.horizontalToggle, row.optional && styles.horizontalToggleActive, 'cy-reflow-optional')}
        onClick={() => dispatch(setReflowRowOptionalOperation(spreadIndex, group.id, row.id, !row.optional))}
        title={
          row.optional
            ? tr('editor-reflow-row-optional-on', 'Optional — this row collapses when its fields are left blank')
            : tr('editor-reflow-row-optional-off', 'Always shown — this row never collapses')
        }
      >
        {row.optional ? '⌄' : '='}
      </button>
      <div className={styles.rowChips}>
        {elementsInRow.length === 0 && (
          <span className={styles.rowEmpty}>{tr('editor-reflow-row-empty', 'Click + to add an element')}</span>
        )}
        {elementsInRow.map((element) => (
          <ElementChip
            key={element.uuid}
            element={element}
            draggable
            onDragStart={handleDragStart}
            onRemove={() => dispatch(removeElementFromReflowRowOperation(spreadIndex, group.id, row.id, element.uuid))}
            showDividerToggle={!!row.horizontalReflow}
            isDivider={!!row.dividerUuids?.includes(element.uuid)}
            onToggleDivider={() =>
              dispatch(
                setReflowRowDividerOperation(
                  spreadIndex,
                  group.id,
                  row.id,
                  element.uuid,
                  !row.dividerUuids?.includes(element.uuid),
                ),
              )
            }
          />
        ))}
        <div className={styles.attachWrap}>
          <button
            type="button"
            className={styles.attachBtn}
            onClick={() => setPickerOpen((v) => !v)}
            title={tr('editor-reflow-add-element', 'Add element to this row')}
          >
            +
          </button>
          {pickerOpen && (
            <ElementPicker
              candidates={candidates}
              onPick={(uuid) => dispatch(addElementToReflowRowOperation(spreadIndex, group.id, row.id, uuid))}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </div>
      </div>
      <button
        type="button"
        className={styles.rowRemove}
        onClick={() => dispatch(removeReflowRowOperation(spreadIndex, group.id, row.id))}
        aria-label={tr('editor-reflow-remove-row', 'Remove row')}
        title={tr('editor-reflow-remove-row', 'Remove row')}
      >
        ×
      </button>
    </div>
  );
}

interface GroupCardProps {
  group: ReflowGroup;
  spreadIndex: number;
  reflowMembers: ReflowMember[];
}

function GroupCard({ group, spreadIndex, reflowMembers }: GroupCardProps) {
  const dispatch = useDispatch();
  const tr = useTr();
  const [name, setName] = useState(group.name);
  const [collapsed, setCollapsed] = useState(false);

  const handleElementDrop = useCallback(
    (targetRowId: string, draggedUuid: number) => {
      const sourceRow = group.rows.find((r) => r.elementUuids.includes(draggedUuid));
      if (sourceRow?.id !== targetRowId) {
        dispatch(addElementToReflowRowOperation(spreadIndex, group.id, targetRowId, draggedUuid));
      }
    },
    [dispatch, group, spreadIndex],
  );

  return (
    <div className={styles.group}>
      <div className={styles.groupHeader}>
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? 'Expand' : 'Collapse'}
        >
          <span className={cn(styles.caret, collapsed && styles.caretCollapsed)}>▾</span>
        </button>
        <span className={styles.groupIcon} aria-hidden>
          ↕
        </span>
        <input
          className={styles.groupName}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            if (name.trim() && name !== group.name) {
              dispatch(renameReflowGroupOperation(spreadIndex, group.id, name.trim()));
            } else if (!name.trim()) {
              setName(group.name);
            }
          }}
          placeholder={tr('editor-reflow-group-name-placeholder', 'Group name')}
        />
        <span className={styles.groupCount}>
          {group.rows.length}{' '}
          {group.rows.length === 1 ? tr('editor-reflow-row-singular', 'row') : tr('editor-reflow-row-plural', 'rows')}
        </span>
        <button
          type="button"
          className={styles.groupRemove}
          onClick={() => dispatch(deleteReflowGroupOperation(spreadIndex, group.id))}
          aria-label={tr('editor-reflow-delete-group', 'Delete reflow group')}
          title={tr('editor-reflow-delete-group', 'Delete reflow group')}
        >
          ×
        </button>
      </div>
      {!collapsed && (
        <div className={styles.groupBody}>
          {group.rows.length === 0 && (
            <div className={styles.groupEmpty}>
              {tr('editor-reflow-group-empty-hint', 'No rows yet. Click Add row to start.')}
            </div>
          )}
          {group.rows.map((row, i) => (
            <RowCard
              key={row.id}
              group={group}
              row={row}
              rowIndex={i}
              spreadIndex={spreadIndex}
              reflowMembers={reflowMembers}
              onElementDrop={handleElementDrop}
            />
          ))}
          <button
            type="button"
            className={styles.addRow}
            onClick={() => dispatch(addReflowRowOperation(spreadIndex, group.id, [], true))}
          >
            + {tr('editor-reflow-add-row', 'Add row')}
          </button>
          <div className={styles.helpText}>
            {tr(
              'editor-reflow-help',
              'When a row’s editable text is blank, the row collapses and the rows below shift up.',
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Renders all reflow groups on the current spread, inline among the other
 * personalized layers. The "create a new group" entry point lives in the
 * existing "Add Personalized Layer" dropdown.
 */
function ReflowGroupsList() {
  const { spreadIndex, reflowGroups, reflowMembers } = useSelector((state) => {
    const idx = getCurrentSpreadIndex(state);
    const spread = state.design.designData?.spreads[idx];
    const media = spread?.pages.flatMap((p) => p.groups.media ?? []) ?? [];
    return {
      spreadIndex: idx,
      reflowGroups: spread?.reflowGroups ?? [],
      reflowMembers: media.filter(isReflowMember),
    };
  }, shallowEqual);

  if (spreadIndex === undefined || spreadIndex < 0 || reflowGroups.length === 0) {
    return null;
  }

  return (
    <div className={styles.list}>
      {reflowGroups.map((group) => (
        <GroupCard key={group.id} group={group} spreadIndex={spreadIndex} reflowMembers={reflowMembers} />
      ))}
    </div>
  );
}

export default React.memo(ReflowGroupsList);
